import { useMemo } from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { Container } from "../../globalStyles";
import {
    useActiveUploads,
    useCrossDeviceUploads,
    useIsRehydratingUploads,
    UploadClientStatus,
} from "@/store";
import { UploadProgressLoadingCard } from "@/components/UploadProgress/UploadProgressLoadingCard";
import { CrossDeviceUploadsList } from "@/components/UploadProgress/CrossDeviceUploadCard";
import { visibleRemoteUploads } from "@/components/UploadProgress/shouldFastPollFromState";
import {
    StatsSection,
    StatsTopBar,
    StatsTitleLeft,
    StatsDivider,
} from "@/components/Stats/Counter/CountersBlock.elements";
import { ClipPage } from "@/components/Clips/Clip.elements";

const InProgressUploadsPage = () => {
    const activeUploads = useActiveUploads();
    const isRehydrating = useIsRehydratingUploads();
    const crossDeviceState = useCrossDeviceUploads();
    const {
        items: crossDeviceItems,
        status: crossDeviceStatus,
        lastFetchedAt: crossDeviceLastFetchedAt,
    } = crossDeviceState;
    // Use the same visibility filter as CrossDeviceUploadsList so the header count matches
    // what's actually rendered — past-byte-transfer rows are hidden in both places. See
    // shouldFastPollFromState.js for the actionable-statuses set.
    const remoteUploads = useMemo(
        () => visibleRemoteUploads(crossDeviceItems),
        [crossDeviceItems]
    );
    const totalVisible = activeUploads.length + remoteUploads.length;

    // "No active uploads" must NOT paint until we've actually asked the
    // server. On a fresh tab / after page reload the initial state is
    // activeUploads=[] and crossDeviceItems=[]; without this guard the empty
    // message flashes for ~500 ms before /sessions/active responds and the
    // real card appears. Contributors have reported reloading up to 3 times
    // because the flash convinced them their 3-hour upload was gone
    // (regression 2026-08). See __tests__/InProgressUploadsPage.test.js.
    //
    // We only need to wait on the cross-device fetch when there are no local
    // uploads to render — local rows are already sourced from IndexedDB
    // rehydration (gated by isRehydrating) so they show immediately.
    //
    // A failed fetch falls through to the empty state so users aren't stuck
    // watching a spinner forever if /sessions/active is genuinely down.
    const awaitingFirstCrossDeviceFetch =
        crossDeviceLastFetchedAt == null && crossDeviceStatus !== "failed";
    // Also show the spinner whenever a fetch is currently in-flight AND
    // there's nothing visible yet — covers the wake-from-long-sleep case
    // (2026-08-05): after 8h suspension the tab wakes with a stale-but-
    // non-null lastFetchedAt so the first gate above wouldn't fire, and
    // the user would see the empty state for the 3-5s the wake fetch
    // takes to complete.
    const isFetchingWithNothingVisible =
        totalVisible === 0 && crossDeviceStatus === "loading";
    const showInitialLoadSpinner =
        totalVisible === 0 &&
        (awaitingFirstCrossDeviceFetch || isFetchingWithNothingVisible);
    // Global Pause All / Resume All + related hooks removed 2026-08-05 — the
    // per-card icon-button (see UploadStateControl) is the sole pause/resume
    // affordance now.

    return (
        <ClipPage>
            <Container>
                <StatsSection>
                    <StatsTopBar>
                        <Box>
                            <StatsTitleLeft>Active uploads</StatsTitleLeft>

                            <Typography
                                variant="body2"
                                sx={{
                                    color: "rgba(247,250,250,0.62)",
                                    mt: 0.5,
                                }}
                            >
                                {isRehydrating
                                    ? "Restoring uploads…"
                                    : showInitialLoadSpinner
                                        ? "Checking for active uploads…"
                                        : totalVisible === 0
                                            ? "No active uploads."
                                            : `${totalVisible} package${
                                                totalVisible === 1 ? "" : "s"
                                            } in progress${
                                                remoteUploads.length > 0 && activeUploads.length > 0
                                                    ? ` (${remoteUploads.length} on another device).`
                                                    : remoteUploads.length > 0
                                                        ? ` on another device.`
                                                        : "."
                                            }`}
                            </Typography>
                        </Box>

                        {/* Top-bar Pause All / Resume All removed 2026-08-05.
                            The per-card icon-button is now the primary pause /
                            resume control (see UploadStateControl); a global
                            button on top of that was redundant clutter. Queued
                            packages don't need bulk action — the icon-button on
                            the active card halts byte flow, and the queue
                            proceeds when the active pauses/completes/cancels. */}
                    </StatsTopBar>

                    <StatsDivider />

                    <CrossDeviceUploadsList />

                    {isRehydrating || showInitialLoadSpinner ? (
                        <Box
                            sx={{
                                py: 6,
                                width: "100%",
                                maxWidth: 880,
                                mx: "auto",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <CircularProgress
                                size={28}
                                thickness={4}
                                sx={{ color: "rgba(247,250,250,0.7)" }}
                            />
                        </Box>
                    ) : totalVisible === 0 ? (
                        <Box
                            sx={{
                                pt: 2,
                                pb: 4,
                                width: "100%",
                                maxWidth: 880,
                                mx: "auto",
                            }}
                        >
                            <Box
                                sx={{
                                    width: "100%",
                                    p: 4,
                                    textAlign: "left",
                                    borderRadius: "10px",
                                    border: "1px solid rgba(255,255,255,0.08)",
                                    backgroundColor: "rgba(41,56,77,0.55)",
                                    color: "rgba(247,250,250,0.72)",
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: "18px",
                                        fontWeight: 500,
                                        color: "rgba(247,250,250,0.9)",
                                    }}
                                >
                                    You don&apos;t have any uploads in progress right now.
                                </Typography>

                                <Typography
                                    variant="body2"
                                    sx={{
                                        mt: 1,
                                        fontSize: "16px",
                                        opacity: 0.7,
                                    }}
                                >
                                    Start a new upload from the Dashboard. Active uploads will appear here.
                                </Typography>
                            </Box>
                        </Box>
                    ) : (
                        <Stack
                            spacing={2}
                            data-testid="upload-active-list"
                            sx={{
                                pt: 2,
                                pb: 4,
                                width: "100%",
                                maxWidth: 880,
                                mx: "auto",
                            }}
                        >
                            {activeUploads.map((upload) => (
                                <Box
                                    key={upload.uploadId}
                                    sx={{
                                        p: 2,
                                        width: "100%",
                                        borderRadius: 2,
                                        backgroundColor: "rgba(41,56,77,0.55)",
                                        border: "1px solid rgba(255,255,255,0.08)",
                                    }}
                                    data-status={upload.status}
                                    data-testid="upload-package-card"
                                >
                                    {(upload.status === UploadClientStatus.UPLOADING ||
                                        upload.status === UploadClientStatus.PAUSED) && (
                                        <UploadProgressLoadingCard upload={upload} />
                                    )}
                                </Box>
                            ))}
                        </Stack>
                    )}
                </StatsSection>
            </Container>
        </ClipPage>
    );
};

export default InProgressUploadsPage;